import { fetchSofiaProxy } from "@/lib/sofiaProxyRequest"

import type { CoviChatItem, CoviThreadMeta } from "../model/types"
import { newThreadMeta } from "./storageThreads"
import { deriveThreadTitle } from "./threadTitle"

const SOFIA_CHAT_ENDPOINT = "/api/sofia/chat"

interface HydratedHistorySnapshot {
  items: CoviChatItem[]
  thread: CoviThreadMeta
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object"
}

export function parseHistorySnapshot(
  value: unknown,
): HydratedHistorySnapshot | null {
  if (!isRecord(value) || !isRecord(value.snapshot)) {
    return null
  }
  const { conversation_id, messages, updated_at } = value.snapshot
  const updatedAt =
    typeof updated_at === "string" ? Date.parse(updated_at) : Number.NaN
  if (
    typeof conversation_id !== "string" ||
    conversation_id.length === 0 ||
    !Array.isArray(messages) ||
    !Number.isFinite(updatedAt)
  ) {
    return null
  }

  const items: CoviChatItem[] = []
  for (const [index, message] of messages.entries()) {
    if (
      !isRecord(message) ||
      (message.role !== "user" && message.role !== "assistant") ||
      typeof message.content !== "string"
    ) {
      return null
    }
    items.push({
      kind: message.role,
      id: `history_${conversation_id}_${index}`,
      text: message.content,
    })
  }
  if (items.length === 0) {
    return null
  }

  return {
    items,
    thread: {
      ...newThreadMeta(conversation_id, updatedAt),
      title: deriveThreadTitle(items),
      updatedAt,
      messageCount: items.length,
    },
  }
}

export async function loadHistorySnapshot(
  organizationSlug?: string | null,
): Promise<HydratedHistorySnapshot | null> {
  const query = organizationSlug
    ? `?organization=${encodeURIComponent(organizationSlug)}`
    : ""
  const response = await fetchSofiaProxy(`${SOFIA_CHAT_ENDPOINT}${query}`, {
    method: "GET",
    cache: "no-store",
  })
  if (!response.ok) {
    return null
  }
  return parseHistorySnapshot(await response.json())
}
